/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import Loader from "../Components/Loader";
import { useSelector } from "react-redux";

const ACCOUNT_ICONS = {
  "/accounts/ledgers": "bi bi-diagram-3",
  "/accounts/ledgers/new": "bi bi-diagram-3",
  "/accounts/sales/new": "bi bi-receipt",
  "/accounts/purchases/new": "bi bi-bag",
  "/accounts/receipts/new": "bi bi-box-arrow-in-down",
  "/accounts/payments/new": "bi bi-box-arrow-up-right",
  "/accounts/vouchers": "bi bi-journal-text",
  "/accounts/reports": "bi bi-journal-bookmark",
  "/accounts/reports/ledger-statement": "bi bi-journal-bookmark",
  "/accounts/reports/day-book": "bi bi-calendar3",
  "/accounts/reports/outstanding": "bi bi-cash-stack",
  "/accounts/reports/aging": "bi bi-hourglass-split",
  "/accounts/reports/payables": "bi bi-wallet2",
  "/accounts/customers": "bi bi-people",
  "/accounts/sales/orders": "bi bi-cart",
  "/accounts/sales/loading-advices": "bi bi-truck",
  "/accounts/sales/invoices": "bi bi-receipt",
  "/accounts/vouchers/credit-note": "bi bi-file-minus",
  "/accounts/vouchers/debit-note": "bi bi-file-plus",
  "/accounts/vouchers/journal": "bi bi-journal",
  "/accounts/cheques": "bi bi-credit-card-2-front",
  "/accounts/portal": "bi bi-person",
};

function menuIcon(item) {
  if (item?.hrefLink && ACCOUNT_ICONS[item.hrefLink]) return ACCOUNT_ICONS[item.hrefLink];
  const children = item?.children || [];
  if (children.some((child) => String(child.hrefLink || "").startsWith("/accounts/"))) return "bi bi-journal-text";
  return item?.icon || "";
}

const Leftside = () => {
  const [getMenu, setMenu] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(true);
  // The one main menu that stays expanded. Mouse leave and page navigation do not close it.
  const [openGroup, setOpenGroup] = useState(() => localStorage.getItem("menu"));

  const userData = useSelector((state) => state?.user);
  const menuData = useSelector((state) => state?.menu);
  // Highlight the item whose link is the current page; fall back to the stored menu/submenu names.
  const { pathname } = useLocation();
  const pathOf = (href) => {
    if (!href) return "";
    const raw = String(href).split("?")[0];
    return raw.length > 1 ? raw.replace(/\/$/, "") : raw;
  };
  const linkActive = (item) => pathOf(item?.hrefLink) !== "" && pathOf(item.hrefLink) === pathname;
  const anyLinkActive = (getMenu || []).some((group) => linkActive(group) || (group.children || []).some(linkActive));
  const groupForPath = (getMenu || []).find((group) => linkActive(group) || (group.children || []).some(linkActive))?.name
    || (!anyLinkActive ? localStorage.getItem("menu") : "");
  useEffect(() => {
    setMenu(menuData?.data || []);
  }, [menuData]);

  // After a submenu navigation, keep that main menu open and bring the selected item into view.
  useEffect(() => {
    if (groupForPath) setOpenGroup(groupForPath);
  }, [pathname, groupForPath]);

  useEffect(() => {
    const sidebar = document.getElementById("sidebar");
    if (!sidebar) return;
    const active = sidebar.querySelector("a.subMenu.active") || sidebar.querySelector("a.nav-link.dropdownMenu.active");
    if (!active) return;
    const panel = active.closest(".nav-content");
    if (panel && !panel.classList.contains("show")) return;
    const bar = sidebar.getBoundingClientRect();
    const item = active.getBoundingClientRect();
    const inView = item.top >= bar.top + 4 && item.bottom <= bar.bottom - 4;
    if (inView) return;
    const top = item.top - bar.top + sidebar.scrollTop - (sidebar.clientHeight / 2) + (item.height / 2);
    sidebar.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }, [pathname, openGroup, getMenu]);

  // Toggle body class for layout shift
  useEffect(() => {
    if (isCollapsed) {
      document.body.classList.add("collapsed-sidebar");
    } else {
      document.body.classList.remove("collapsed-sidebar");
    }
  }, [isCollapsed]);

  const LoadParent = ({ value }) => {
    const selectedMenu = localStorage.getItem("menu");
    const selectedSubMenu = localStorage.getItem("submenu");

    if (value.children?.length > 0) {
      const routeSelected = (value.children || []).some(linkActive)
        || (!anyLinkActive && value.name === selectedMenu);
      const expanded = openGroup === value.name;
      const childItem = value.children.map((child_item) => {
        const childActive = linkActive(child_item)
          || (!anyLinkActive && value.name === selectedMenu && child_item.name === selectedSubMenu);
        const specCase = childActive
          ? "nav-link subMenu active"
          : "subMenu collapsed";

        return (
          <li key={child_item.name} className="nav-item">
            <Link
              to={child_item.hrefLink}
              className={specCase}
              onClick={() => {
                localStorage.setItem("menu", value.name);
                localStorage.setItem("submenu", child_item.name);
                setOpenGroup(value.name);
              }}
            >
              <i className={menuIcon(child_item)}></i>
              <span>{child_item.name}</span>
            </Link>
          </li>
        );
      });

      const subMenuStatus = expanded ? "nav-content collapse show" : "nav-content collapse";

      return (
        <li key={value.name} className="nav-item">
          <a
            href="#"
            className={`nav-link${routeSelected ? " dropdownMenu active" : " collapsed"}${expanded ? " menu-open" : " collapsed"}`}
            onClick={(e) => {
              e.preventDefault();
              setOpenGroup((current) => (current === value.name ? "" : value.name));
            }}
          >
            <i className={menuIcon(value)}></i>
            <span>{value.name}</span>
            <i className="bi bi-chevron-down ms-auto"></i>
          </a>

          <ul
            id={value.data_bs_target}
            className={subMenuStatus}
          >
            {childItem}
          </ul>
        </li>
      );
    } else {
      const leafActive = linkActive(value) || (!anyLinkActive && value.name === selectedMenu);
      const specCase = leafActive ? "nav-link active" : "nav-link collapsed";

      return (
        <>
          <li key={value.name} className="nav-item">
            <Link
              to={value.hrefLink}
              className={specCase}
              onClick={() => {
                localStorage.setItem("menu", value.name);
                localStorage.removeItem("submenu");
                setOpenGroup("");
              }}
            >
              <i className={menuIcon(value)}></i>
              <span>{value.name}</span>
            </Link>
          </li>
          <Outlet />
        </>
      );
    }
  };

  return (
    <div
      className="main-sidebar"
      onMouseEnter={() => setIsCollapsed(false)}
      onMouseLeave={() => setIsCollapsed(true)}
    >
      <aside id="sidebar" className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
        <nav>
          <ul className="sidebar-nav" id="sidebar-nav">
            <Loader isLoading={!userData?.status} className={"text-center"} />
            {userData?.status &&
              getMenu?.length > 0 &&
              getMenu.map((value, index) => (
                <LoadParent key={index} value={value} />
              ))}
          </ul>
        </nav>
      </aside>
    </div>
  );
};

export default Leftside;